import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import DetailFrame from '../shared/Frames/DetailFrame';
import Button from '../shared/TailwindComponents/Button';
import {
  createReview,
  extractPlaybook,
} from '../../store/actions/Review/ReviewActions';
import { fetchLinkableProjects } from '../../store/actions/Projects/ProjectActions';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, createPlaybookFormTourSteps } from '../shared/FeatureTour';
import TemplateTypeSwitcher from '../Templates/TemplateTypeSwitcher';
import PlaybookSourceUpload from './PlaybookSourceUpload';
import PlaybookExtractionProgress from './PlaybookExtractionProgress';
import { StagedPlaybookFile } from './playbookExtraction';
import { usePlaybookExtractionPubSub } from './usePlaybookExtractionPubSub';

/** Let a clean run's "done" state land before leaving the screen. */
const NAVIGATE_DELAY_MS = 1200;

const CreatePlaybookForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);
  /** Set once the playbook row exists - this is also the PubSub group key. */
  const [playbookUuid, setPlaybookUuid] = useState<string | null>(null);

  /**
   * Live generation progress, from the SAME hook the playbook detail page
   * uses: `ai_processing` hub, group `playbook_{uuid}`. This screen used to
   * hand-roll its own listener with a correlation-id filter and a hardcoded
   * four-step list, which went blank the moment the engine changed its
   * pipeline.
   */
  const extraction = usePlaybookExtractionPubSub(playbookUuid || undefined, !!playbookUuid);
  const { beginRun: beginExtraction, reset: resetExtraction } = extraction;

  // Fetch linkable projects on mount
  React.useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsResp = await dispatch(fetchLinkableProjects() as any);
        if (projectsResp && Array.isArray(projectsResp)) {
          setProjectOptions(projectsResp.map((p: any) => ({ value: p.uuid, label: p.title })));
        }
      } catch (error: any) {
        console.error('[CreatePlaybookForm] Error loading projects:', error?.message);
      }
    };
    loadProjects();
  }, [dispatch]);

  const isGenerating = !!playbookUuid;

  // ─── Tour ──────────────────────────────────────────────────────────
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'create-playbook-form',
    steps: createPlaybookFormTourSteps,
    readyToShow: !isGenerating && !isSubmitting,
    autoStartDelay: 600,
  });

  /**
   * A clean run leaves for the playbook. A run that couldn't read some of the
   * documents stays put, so the notice naming those files isn't thrown away
   * by the navigation that follows it.
   */
  useEffect(() => {
    if (extraction.status !== 'completed' || !playbookUuid) return;
    if (extraction.failedDocuments.length > 0) return;
    const timer = setTimeout(() => history.push(`/playbooks/${playbookUuid}`), NAVIGATE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [extraction.status, extraction.failedDocuments.length, playbookUuid, history]);

  const breadcrumbs = useMemo(
    () => [
      { name: t('Templates'), href: '/templates?type=playbooks', current: false },
      { name: t('New playbook'), href: '/templates/add?type=playbooks', current: true },
    ],
    [t],
  );

  const schema = useMemo(
    () =>
      new FormSchema('createPlaybook', [
        new FormField('name', 'text', false, t('Playbook name')),
        new FormField('document', 'text', true, t('Playbook documents'), {
          customValidator: (value: any) => {
            if (!value || (Array.isArray(value) && value.length === 0)) {
              return { isValid: false, error: t('Upload at least one document to generate the playbook from') };
            }
            return { isValid: true };
          },
        }),
      ]),
    [t],
  );

  const sections: BaseFormSectionConfig[] = useMemo(
    () => [
      {
        dataTourId: 'playbook-form-name',
        title: t('Playbook details'),
        description: t('Name the playbook, or let AI take the name from the documents you upload.'),
        fields: [
          {
            name: 'name',
            type: 'input',
            label: t('Playbook name'),
            placeholder: t('Optional — AI will extract the name from the document'),
            required: false,
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
      {
        dataTourId: 'playbook-form-upload',
        title: t('Upload playbook documents'),
        description: t(
          "Upload your firm's playbook, negotiation guidelines, clause bank or annexes — add as many as apply. AI reads them together and extracts clause rules, global rules, agreement types, and other metadata automatically.",
        ),
        fields: [
          {
            name: 'document',
            type: 'custom',
            // The section is titled "Upload Playbook Documents" and holds this
            // one field, so a "Documents" label under it just says it twice.
            label: '',
            required: true,
            gridSize: 'full',
            customComponent: PlaybookSourceUpload,
            customProps: { density: 'canvas' },
          } as BaseFormFieldConfig,
        ],
      },
      {
        dataTourId: 'playbook-form-collaborators',
        title: t('Access'),
        description: t('Optionally invite team members who should have access to this playbook.'),
        fields: [
          {
            name: 'collaborators',
            type: 'collaboratorSelector' as any,
            // The section is already titled "Access" - one name is enough.
            label: '',
            required: false,
            gridSize: 'full',
            customProps: {
              allowedPermissions: ['edit', 'view'],
              showOwnerSection: false,
            },
          } as BaseFormFieldConfig,
        ],
      },
      ...(projectOptions.length > 0 ? [{
        title: t('Link to project'),
        description: t('Optionally link this playbook to an existing project.'),
        fields: [
          {
            name: 'project_uuid',
            type: 'dropdown' as const,
            label: t('Project'),
            placeholder: t('Select a project'),
            required: false,
            gridSize: 'full' as const,
            customProps: {fullWidth: true} as any,
            options: projectOptions,
          } as BaseFormFieldConfig,
        ],
      }] : []),
    ],
    [t, projectOptions],
  );

  // ── Submit: create the playbook, then post ALL staged documents ────
  const handleSubmit = useCallback(
    async (formData: Record<string, any>) => {
      setIsSubmitting(true);

      try {
        const name = formData.name?.trim() || 'Untitled Playbook';
        const collaborators = (formData.collaborators || []).map((c: any) => ({
          collaborator_uuid: c.collaborator_uuid || c.user_id,
          access: c.access || 'view',
        }));
        const createPayload: any = { name, collaborators };
        if (formData.project_uuid) {
          createPayload.project_uuid = formData.project_uuid;
        }

        const result = await dispatch(createReview(createPayload) as any);
        if (!result?.uuid) {
          setIsSubmitting(false);
          return;
        }

        const staged: StagedPlaybookFile[] = Array.isArray(formData.document)
          ? formData.document
          : [];
        const files = staged.map((s) => s.file);

        if (!files.length) {
          setIsSubmitting(false);
          history.replace(`/playbooks/${result.uuid}`);
          return;
        }

        // Seed the per-file rows and join the group BEFORE the upload, so the
        // first engine event has somewhere to land.
        beginExtraction(files.map((f) => f.name));
        setPlaybookUuid(result.uuid);

        const extractResult = await dispatch(extractPlaybook(result.uuid, files) as any);
        if (!extractResult) {
          // The POST never landed - nothing will arrive on the group.
          setPlaybookUuid(null);
          resetExtraction();
          setIsSubmitting(false);
        }
      } catch (err) {
        setIsSubmitting(false);
        setPlaybookUuid(null);
        resetExtraction();
      }
    },
    [dispatch, history, beginExtraction, resetExtraction],
  );

  const handleCancel = useCallback(() => {
    history.push('/templates?type=playbooks');
  }, [history]);

  // ── Progress view: live generation, on brand primitives ──────────
  if (isGenerating || isSubmitting) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} actions={[]}>
        {/* Generation is the only thing on this screen, so it sits in the
            middle of it rather than in the top-left corner. */}
        <PlaybookExtractionProgress
          run={extraction}
          title={t('Generating playbook…')}
          layout="centered"
          onDismissFailedDocuments={extraction.dismissFailedDocuments}
          footer={
            <>
              {extraction.status === 'completed' && playbookUuid && (
                <Button variant="primary" onClick={() => history.push(`/playbooks/${playbookUuid}`)}>
                  {t('View playbook')}
                </Button>
              )}
              {extraction.status !== 'completed' && (
                <Button
                  variant="outline"
                  onClick={() =>
                    // Generation continues server-side; leaving only stops watching.
                    history.push(playbookUuid ? `/playbooks/${playbookUuid}` : '/templates?type=playbooks')
                  }
                >
                  {t('Continue in background')}
                </Button>
              )}
            </>
          }
        />
      </DetailFrame>
    );
  }

  return (
    <DetailFrame breadcrumbs={breadcrumbs} actions={[]}>
      <BaseForm
        title={t('Create playbook')}
        description={t(
          "Upload your firm's playbook documents and AI will extract all clause rules, global rules, and metadata automatically.",
        )}
        /* This form is only ever reached from `/templates/add?type=playbooks`
           - it lives under Review because playbooks belong to reviews, but a
           playbook is a template library and is created alongside the others. */
        hideHeader
        leadIn={
          <TemplateTypeSwitcher
            current="playbooks"
            variant="header"
            title={t('Create playbook')}
            description={t(
              "Upload your firm's playbook documents and AI will extract all clause rules, global rules, and metadata automatically.",
            )}
          />
        }
        sections={sections}
        schema={schema}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitLabel={isSubmitting ? t('Creating...') : t('Create & generate')}
        cancelLabel={t('Cancel')}
        maxWidth="max-w-3xl"
      />
      <FeatureTour
        steps={createPlaybookFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </DetailFrame>
  );
};

export default CreatePlaybookForm;
